/**
 * Sidebar Component
 *
 * Collapsible left navigation. Shows role-based menu items (doctor vs
 * attorney vs admin), unread notification badges, and legal/support
 * links. Highlights the active view.
 *
 * @module components/sidebar
 */

import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileText,
  FolderOpen,
  LayoutDashboard,
  Receipt,
  Settings,
  Shield,
  ShieldAlert,
} from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { apiClient } from '../api';
import { getLearningCenterBaseUrl } from '../constants/learning-center';
import { useAuth } from '../contexts/auth-context';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const LEGAL_LINKS = [
  { label: 'Terms', href: '/terms', ariaLabel: 'Terms of Service (opens in new tab)' },
  { label: 'Privacy', href: '/privacy', ariaLabel: 'Privacy Policy (opens in new tab)' },
] as const;

interface SidebarProps {
  currentView: string;
  onViewChange: (view: string) => void;
}

export function Sidebar({ currentView, onViewChange }: SidebarProps) {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);

  // Fetch notification count on component mount and set up periodic refresh
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await apiClient.getPendingRequestsCount(user?.vanityUrl);
        setNotificationCount(response.count);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
        // Keep current count on error
      }
    };

    // Initial fetch
    fetchNotificationCount();

    // Set up periodic refresh every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);

    return () => clearInterval(interval);
  }, [user?.vanityUrl]);

  // Build navigation array based on user role
  const isAdmin = user?.role === 'ADMIN';
  const isTeamMember = user?.role === 'TEAM_MEMBER';

  const navigation = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    {
      id: 'requests',
      label: 'Pre-Screening Requests',
      icon: FileText,
      badge: notificationCount > 0 ? notificationCount : undefined,
    },
    { id: 'cases', label: 'Cases & Records', icon: FolderOpen },
    ...(!isTeamMember ? [{ id: 'invoices', label: 'Invoices', icon: Receipt }] : []),
    ...(!isTeamMember ? [{ id: 'billing', label: 'Billing', icon: DollarSign }] : []),
    { id: 'settings', label: 'Settings', icon: Settings },
    // Admin-only navigation
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: ShieldAlert }] : []),
  ];

  return (
    <div
      className={`flex h-full flex-col border-r border-[#EAE0CB]/8 bg-[#3A2718] transition-all duration-300 lg:h-screen ${collapsed ? 'w-16' : 'w-64'}`}
    >
      {/* Header */}
      <div
        className={`flex items-center border-b border-[#EAE0CB]/8 p-4 ${collapsed ? 'justify-center' : 'justify-between'}`}
      >
        {!collapsed && (
          <div className="flex items-center gap-2.5">
            <Shield className="h-6 w-6 text-[#A66B32]" strokeWidth={1.6} />
            <span className="font-display text-[18px] font-medium tracking-tight text-[#EAE0CB]">
              ExpertMD
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className={`hidden text-[#EAE0CB]/60 hover:bg-[#EAE0CB]/8 hover:text-[#EAE0CB] lg:flex ${collapsed ? 'p-2' : ''}`}
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3">
        {navigation.map((item) => {
          const isActive = currentView === item.id;
          return (
            <Button
              key={item.id}
              variant="ghost"
              className={`w-full gap-3 rounded-sm font-editorial text-[13.5px] transition-colors ${
                collapsed ? 'justify-center px-0' : 'justify-start'
              } ${
                isActive
                  ? 'bg-[#EAE0CB]/12 text-[#EAE0CB] hover:bg-[#EAE0CB]/16 hover:text-[#EAE0CB]'
                  : 'text-[#EAE0CB]/70 hover:bg-[#EAE0CB]/8 hover:text-[#EAE0CB]'
              }`}
              onClick={() => onViewChange(item.id)}
            >
              <item.icon
                className={`h-5 w-5 flex-shrink-0 ${collapsed ? 'mx-auto' : ''} ${
                  isActive ? 'text-[#A66B32]' : ''
                }`}
                strokeWidth={1.5}
              />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge && (
                    <Badge className="border-transparent bg-[#A66B32] font-editorial text-white hover:bg-[#8A5626]">
                      {item.badge}
                    </Badge>
                  )}
                </>
              )}
            </Button>
          );
        })}
      </nav>

      {/* Help + Legal Links */}
      {!collapsed && (
        <div className="space-y-2 border-t border-[#EAE0CB]/8 p-4">
          <a
            href={getLearningCenterBaseUrl()}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Learning Center (opens in new tab)"
            className="flex items-center gap-2 font-editorial text-xs text-[#EAE0CB]/60 transition-colors hover:text-[#A66B32]"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Help
          </a>
          <div className="flex gap-3 font-editorial text-xs text-[#EAE0CB]/45">
            {LEGAL_LINKS.map((link, idx) => (
              <Fragment key={link.label}>
                {idx > 0 && <span>·</span>}
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.ariaLabel}
                  className="transition-colors hover:text-[#EAE0CB]/80"
                >
                  {link.label}
                </a>
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
